/**
 * PdfCanvasViewer — renders PDF pages as canvas elements using pdfjs-dist.
 * Avoids Chrome's built-in PDF viewer (which can freeze iframes).
 *
 * Controlled: the host owns viewMode + zoom (and shows the toolbar). This component
 * renders only the page canvases and reports page count / current page / scale back.
 *
 * View modes:
 *   - 'fit'    — single column, each page stretched to the available width (default)
 *   - 'scroll' — actual size, single column, pan horizontally (canvas not clamped)
 *   - 'two'    — two pages side by side
 *   - 'grid'   — thumbnails of every page in a grid
 * Pages render lazily as they scroll into view (IntersectionObserver).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import './PdfCanvasViewer.css';

// Configure worker — use local copy in /public
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export type ViewMode = 'fit' | 'scroll' | 'two' | 'grid';
export const VIEW_MODES: { key: ViewMode; label: string; title: string }[] = [
  { key: 'fit',    label: 'Auto fit',   title: 'Auto-fit each page to the available width (default)' },
  { key: 'scroll', label: 'Scroll',     title: 'Actual size — pan with the bottom scrollbar or ← →' },
  { key: 'two',    label: 'Two-up',     title: 'Two pages side by side' },
  { key: 'grid',   label: 'Thumbnails', title: 'All pages as a grid of thumbnails' },
];

// Auto fit: pages narrower than this (PDF points; ~5.5in) are treated as receipts
// and shown at no more than NARROW_PAGE_MAX_SCALE (1.0 = 72 dpi) rather than pane width.
const NARROW_PAGE_PT = 400;
const NARROW_PAGE_MAX_SCALE = 2;
// Cap on the device-pixel multiplier used for the canvas backing store.
const MAX_PIXEL_RATIO = 3;

interface PdfCanvasViewerProps {
  /** Raw PDF bytes as Uint8Array */
  pdfBytes: Uint8Array | null;
  /** Optional title for accessibility */
  title?: string;
  /** View mode (host-controlled) */
  viewMode?: ViewMode;
  /** User zoom multiplier on top of the auto-fit (host-controlled) */
  zoom?: number;
  /** Reports total page count up to the host toolbar */
  onNumPages?: (n: number) => void;
  /** Reports the top-most visible page up to the host toolbar */
  onCurrentPageChange?: (p: number) => void;
  /** Reports the effective scale (percent) up to the host toolbar */
  onScaleChange?: (pct: number) => void;
  /** Page to scroll to once a NEW pdfBytes has loaded (e.g. the page just rotated) — default the top */
  openAtPage?: number;
}

const PdfCanvasViewer: React.FC<PdfCanvasViewerProps> = ({
  pdfBytes, title, viewMode = 'fit', zoom = 1,
  onNumPages, onCurrentPageChange, onScaleChange, openAtPage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [baseScale, setBaseScale] = useState(1.0);   // auto-fit scale for the active mode
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const pdfDocRef = useRef<any>(null);
  const renderingRef = useRef<Set<number>>(new Set());
  const visibleRef = useRef<Set<number>>(new Set());

  const effectiveScale = baseScale * zoom;

  // Surface page count / current page / scale to the host toolbar.
  useEffect(() => { if (onNumPages) onNumPages(numPages); }, [numPages, onNumPages]);
  useEffect(() => { if (onCurrentPageChange) onCurrentPageChange(currentPage); }, [currentPage, onCurrentPageChange]);
  useEffect(() => { if (onScaleChange) onScaleChange(Math.round(effectiveScale * 100)); }, [effectiveScale, onScaleChange]);

  // Load the PDF document
  useEffect(() => {
    if (!pdfBytes || pdfBytes.length === 0) {
      setLoading(false);
      setError('No PDF data');
      return;
    }

    setLoading(true);
    setError('');
    setCurrentPage(1);
    setNumPages(0);
    visibleRef.current.clear();

    pdfjsLib.getDocument({ data: pdfBytes.slice() }).promise
      .then(pdf => {
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setLoading(false);
      })
      .catch(err => {
        console.warn('[PdfCanvasViewer] Failed to load PDF:', err);
        setError('Failed to load PDF');
        setLoading(false);
      });

    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [pdfBytes]);

  // Compute the auto-fit scale for the active mode.
  const recomputeScale = useCallback(async () => {
    const pdf = pdfDocRef.current;
    const container = containerRef.current;
    if (!pdf || !container || numPages === 0) return;

    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1.0 });

    const avail = container.clientWidth - 24;        // minus padding
    let s = 1.0;
    if (viewMode === 'fit') {
      s = avail / vp.width;                            // fill the full pane width (no cap → true stretch)
      // Narrow pages (till receipts) would stretch to a huge, mostly off-screen strip —
      // cap them at a comfortable reading size instead.
      if (vp.width < NARROW_PAGE_PT) s = Math.min(s, NARROW_PAGE_MAX_SCALE);
    } else if (viewMode === 'scroll') {
      s = 1.0;                                         // actual size; page may exceed the pane → pan horizontally
    } else if (viewMode === 'two') {
      s = (avail - 16) / 2 / vp.width;                 // two columns + gap, each filling its half
    } else {
      const cols = Math.max(2, Math.floor(avail / 190));
      const thumbW = (avail - (cols - 1) * 14) / cols;
      s = thumbW / vp.width;
    }
    setBaseScale(s > 0 ? s : 1.0);
  }, [numPages, viewMode]);

  useEffect(() => { recomputeScale(); }, [recomputeScale]);

  // Re-fit when the pane is resized.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { recomputeScale(); });
    ro.observe(container);
    return () => ro.disconnect();
  }, [recomputeScale]);

  // Render a single page to its canvas at the current effective scale.
  const renderPage = useCallback(async (pageNum: number) => {
    const pdf = pdfDocRef.current;
    if (!pdf || renderingRef.current.has(pageNum)) return;

    const canvas = document.getElementById(`pdf-canvas-${pageNum}`) as HTMLCanvasElement;
    if (!canvas) return;
    if (canvas.dataset.renderedScale === String(effectiveScale)) return;  // already current

    renderingRef.current.add(pageNum);
    try {
      const page = await pdf.getPage(pageNum);
      // Draw at the screen's real pixel density (and CSS-size the canvas back down) so
      // text stays sharp on high-DPI / browser-zoomed displays instead of being upscaled.
      const dpr = Math.min(MAX_PIXEL_RATIO, window.devicePixelRatio || 1);
      const cssWidth = page.getViewport({ scale: effectiveScale }).width;
      const viewport = page.getViewport({ scale: effectiveScale * dpr });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.width = `${Math.floor(cssWidth)}px`;
      canvas.dataset.renderedScale = String(effectiveScale);

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport }).promise;
    } catch (err) {
      console.warn(`[PdfCanvasViewer] Error rendering page ${pageNum}:`, err);
    } finally {
      renderingRef.current.delete(pageNum);
    }
  }, [effectiveScale]);

  // When the scale changes, invalidate every canvas so the visible ones re-render sharp.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.querySelectorAll('canvas').forEach(c => { (c as HTMLCanvasElement).dataset.renderedScale = ''; });
  }, [effectiveScale]);

  // Re-open at the requested page after the bytes were replaced (rotate keeps your place).
  const openAtRef = useRef(openAtPage);
  openAtRef.current = openAtPage;
  useEffect(() => {
    const target = openAtRef.current;
    if (loading || numPages === 0 || !target || target <= 1) return;
    const el = containerRef.current?.querySelector(`.pcv-page-wrapper[data-page="${Math.min(target, numPages)}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'start' });
  }, [loading, numPages, pdfBytes]);

  // Lazily render pages as they scroll into view — works for every layout.
  useEffect(() => {
    if (numPages === 0 || loading) return;
    const container = containerRef.current;
    if (!container) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        const pageNum = Number((e.target as HTMLElement).dataset.page);
        if (e.isIntersecting) { visibleRef.current.add(pageNum); renderPage(pageNum); }
        else visibleRef.current.delete(pageNum);
      });
      if (visibleRef.current.size) setCurrentPage(Math.min(...Array.from(visibleRef.current)));
    }, { root: container, rootMargin: '300px 0px', threshold: 0.01 });

    container.querySelectorAll('.pcv-page-wrapper').forEach(w => io.observe(w));
    return () => io.disconnect();
  }, [numPages, loading, renderPage, viewMode, effectiveScale]);

  if (loading) {
    return (
      <div className="pcv-container pcv-loading">
        <div className="pcv-spinner" />
        <p>Loading document...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pcv-container pcv-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="pcv-container">
      <div
        className={`pcv-pages pcv-mode-${viewMode}`}
        ref={containerRef}
        role="document"
        aria-label={title || 'PDF document'}
      >
        {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
          <div key={pageNum} className="pcv-page-wrapper" data-page={pageNum}>
            <canvas
              id={`pdf-canvas-${pageNum}`}
              className="pcv-page-canvas"
            />
            <div className="pcv-page-number">Page {pageNum}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PdfCanvasViewer;
